import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Sparkles, Shield, Clock, Award, Phone, Star } from 'lucide-react';

const WHATSAPP_NUMBER = '+919876543210';
const WHATSAPP_MESSAGE = 'Hi! I would like to book a service.';

const SERVICES = [
  {
    id: 'shoe-cleaning',
    title: 'Shoe Cleaning',
    description: 'Professional deep cleaning for all types of luxury footwear.',
    image: 'https://images.pexels.com/photos/34510831/pexels-photo-34510831.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    id: 'sneaker-restoration',
    title: 'Sneaker Restoration',
    description: 'Specialized care for premium and limited edition sneakers.',
    image: 'https://images.pexels.com/photos/6965875/pexels-photo-6965875.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    id: 'handbag-cleaning',
    title: 'Handbag Care',
    description: 'Expert cleaning and restoration for designer handbags.',
    image: 'https://images.unsplash.com/photo-1598099947145-e85739e7ca28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsZWF0aGVyJTIwaGFuZGJhZ3xlbnwwfHx8fDE3NzQ2NTYwNDV8MA&ixlib=rb-4.1.0&q=85',
  },
];

const TESTIMONIALS = [
  {
    name: 'Priya Sharma',
    role: 'Fashion Designer',
    content: 'Drycleanspace transformed my vintage Chanel bag. It looks brand new! Their attention to detail is remarkable.',
  },
  {
    name: 'Rahul Mehta',
    role: 'Business Executive',
    content: 'My limited edition Jordans were in terrible condition. The team restored them perfectly. Highly recommended!',
  },
  {
    name: 'Ananya Verma',
    role: 'Luxury Collector',
    content: 'Professional service with premium results. They understand the value of luxury items and treat them accordingly.',
  },
];

const PROCESS_STEPS = [
  { number: '01', title: 'Book', description: 'Schedule your pickup via WhatsApp or our contact form.' },
  { number: '02', title: 'Pickup', description: 'We collect your items from your doorstep with care.' },
  { number: '03', title: 'Restore', description: 'Our experts clean and restore using premium techniques.' },
  { number: '04', title: 'Deliver', description: 'Receive your items looking brand new.' },
];

const FadeInSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Home = () => {
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1691480250099-a63081ecfcb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBsZWF0aGVyJTIwaGFuZGJhZ3xlbnwwfHx8fDE3NzQ2NTYwNDV8MA&ixlib=rb-4.1.0&q=85"
            alt="Luxury leather handbag"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/50" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 pt-40">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-2xl"
          >
            <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-6 block">
              Premium Care Services
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1A1A1A] leading-tight mb-6">
              Restore Luxury.<br />
              <span className="text-[#C5A059]">Preserve Style.</span>
            </h1>
            <p className="font-body text-base md:text-lg text-[#66635B] leading-relaxed mb-10 max-w-xl">
              Expert cleaning and restoration for your most treasured shoes and handbags.
              We bring back the elegance your luxury items deserve.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="btn-primary flex items-center justify-center gap-2"
              >
                <Phone size={18} />
                Book Now
              </a>
              <Link
                to="/contact"
                data-testid="hero-cta-quote"
                className="btn-secondary flex items-center justify-center gap-2"
              >
                Get Quote
                <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-[#FAFAFA] border-y border-[#EAE5D9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: Sparkles, label: 'Premium Quality' },
              { icon: Shield, label: 'Insured Service' },
              { icon: Clock, label: 'Quick Turnaround' },
              { icon: Award, label: '5000+ Items Restored' },
            ].map((item, index) => (
              <div key={index} className="flex items-center justify-center gap-3">
                <item.icon size={24} className="text-[#C5A059]" />
                <span className="font-body text-sm text-[#1A1A1A]">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-16">
              <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                Our Services
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A]">
                Premium Care for Premium Items
              </h2>
            </div>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {SERVICES.map((service, index) => (
              <FadeInSection key={service.id} delay={index * 0.1}>
                <Link
                  to="/services"
                  data-testid={`service-card-${service.id}`}
                  className="group block"
                >
                  <div className="service-card overflow-hidden">
                    <div className="img-hover aspect-[4/3]">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-2">
                        {service.title}
                      </h3>
                      <p className="font-body text-sm text-[#66635B] mb-4">
                        {service.description}
                      </p>
                      <span className="font-body text-xs uppercase tracking-[0.15em] text-[#C5A059] flex items-center gap-2 group-hover:gap-4 transition-all">
                        Learn More <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              </FadeInSection>
            ))}
          </div>

          <FadeInSection delay={0.3}>
            <div className="text-center mt-12">
              <Link
                to="/services"
                data-testid="view-all-services"
                className="btn-secondary inline-flex items-center gap-2"
              >
                View All Services
                <ArrowRight size={16} />
              </Link>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* How It Works */}
      <section data-testid="process-section" className="section-padding bg-[#F5F0E6]">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-16">
              <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                How It Works
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A]">
                Simple, Seamless Process
              </h2>
            </div>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-4">
            {PROCESS_STEPS.map((step, index) => (
              <FadeInSection key={step.number} delay={index * 0.1}>
                <div className="text-center relative">
                  <div className="w-16 h-16 mx-auto mb-6 border border-[#C5A059] flex items-center justify-center">
                    <span className="font-heading text-2xl font-semibold text-[#C5A059]">
                      {step.number}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-[#1A1A1A] mb-2">
                    {step.title}
                  </h3>
                  <p className="font-body text-sm text-[#66635B]">
                    {step.description}
                  </p>
                  {index < PROCESS_STEPS.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-px bg-[#EAE5D9]" />
                  )}
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeInSection>
            <div className="text-center mb-16">
              <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
                Testimonials
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#1A1A1A]">
                Loved by Our Clients
              </h2>
            </div>
          </FadeInSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial, index) => (
              <FadeInSection key={testimonial.name} delay={index * 0.1}>
                <div
                  data-testid={`testimonial-${index}`}
                  className="bg-[#FAFAFA] border border-[#EAE5D9] p-8 relative"
                >
                  <div className="absolute top-4 left-6 quote-mark">"</div>
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} className="fill-[#C5A059] text-[#C5A059]" />
                    ))}
                  </div>
                  <p className="font-body text-sm text-[#66635B] leading-relaxed mb-6 relative z-10">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <p className="font-heading text-lg font-semibold text-[#1A1A1A]">
                      {testimonial.name}
                    </p>
                    <p className="font-body text-xs text-[#C5A059] uppercase tracking-wider">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="section-padding bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto text-center">
          <FadeInSection>
            <span className="font-body text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-4 block">
              Ready to Restore?
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-6">
              Give Your Luxury Items the Care They Deserve
            </h2>
            <p className="font-body text-base text-white/60 mb-10 max-w-2xl mx-auto">
              Book your service today and experience the Drycleanspace difference.
              Premium care, exceptional results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp"
                className="bg-[#C5A059] text-white px-8 py-4 font-body text-sm uppercase tracking-wider hover:bg-[#D4AF37] transition-colors flex items-center justify-center gap-2"
              >
                <Phone size={18} />
                Book via WhatsApp
              </a>
              <Link
                to="/contact"
                data-testid="cta-contact"
                className="border border-white/30 text-white px-8 py-4 font-body text-sm uppercase tracking-wider hover:bg-white hover:text-[#1A1A1A] transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
};

export default Home;
